import axios from 'axios';

/**
 * Document counts per person from GET /trees/:treeId/documents
 */
export async function fetchDocumentCountsByTree(apiBase, treeId) {
  const API = `${apiBase}/api`;
  const counts = {};
  try {
    const { data } = await axios.get(`${API}/trees/${treeId}/documents`);
    (data || []).forEach((doc) => {
      const id = doc.individual_id;
      if (!id) return;
      counts[id] = (counts[id] || 0) + 1;
    });
  } catch {
    return {};
  }
  return counts;
}

/**
 * Story card counts per person (parallel GET /individuals/:id/stories).
 */
export async function fetchStoryCountsByPersonIds(apiBase, personIds) {
  const API = `${apiBase}/api`;
  const ids = [...new Set((personIds || []).filter(Boolean))];
  const sc = {};
  await Promise.all(
    ids.map(async (id) => {
      try {
        const { data } = await axios.get(`${API}/individuals/${id}/stories`);
        sc[id] = Array.isArray(data) ? data.length : 0;
      } catch {
        sc[id] = 0;
      }
    })
  );
  return sc;
}

/** Single-tree: stories + documents for a known list of individuals */
export async function fetchPersonActivityMaps(apiBase, treeId, people) {
  if (!treeId || !people?.length) {
    return { storyCounts: {}, documentCounts: {} };
  }
  const ids = people.map((p) => p.id).filter(Boolean);
  const [documentCounts, storyCounts] = await Promise.all([
    fetchDocumentCountsByTree(apiBase, treeId),
    fetchStoryCountsByPersonIds(apiBase, ids),
  ]);
  return { storyCounts, documentCounts };
}

/**
 * Multiple trees (e.g. DNA linking): merge document counts per tree; stories per person id.
 */
export async function fetchPersonActivityMapsForPeopleList(apiBase, people) {
  if (!people?.length) {
    return { storyCounts: {}, documentCounts: {} };
  }
  const ids = people.map((p) => p.id).filter(Boolean);
  const treeIds = [...new Set(people.map((p) => p.tree_id).filter(Boolean))];
  const [storyCounts, ...docParts] = await Promise.all([
    fetchStoryCountsByPersonIds(apiBase, ids),
    ...treeIds.map((tid) => fetchDocumentCountsByTree(apiBase, tid)),
  ]);
  const documentCounts = {};
  docParts.forEach((part) => {
    Object.assign(documentCounts, part);
  });
  return { storyCounts, documentCounts };
}
